import { useCallback, useEffect, useMemo, useState } from 'react';

import { useResource, skipToken } from 'pulse/react';

import {
  CrmSearchOptions,
  CrmSearchVariables,
} from 'framework-listing-lib/types';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import listingLibPerformanceTracking, {
  MEASURE_MARK_NAMES,
} from 'framework-listing-lib/internal/utils/performanceTracking';
import {
  CrmSearchAllProperties,
  getPulseArgs,
  transformPulseResponseToCrmSearchResponse,
} from 'framework-listing-lib/internal/resources/pulseCrmSearch';

function getStatusCodeDimension(error: unknown) {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? String(status) : 'unknown';
}

function useTrackPulsePerf({
  enabled,
  hasData,
  trackPerformance = false,
}: {
  enabled: boolean;
  hasData: boolean;
  trackPerformance?: boolean;
}) {
  'use memo';
  const shouldTrack = enabled && trackPerformance;

  useEffect(() => {
    if (shouldTrack) {
      listingLibPerformanceTracking
        .getPerformanceTracking(MEASURE_MARK_NAMES.CRM_SEARCH)
        .markTimeStart();
    }
  }, [shouldTrack]);

  useEffect(() => {
    if (shouldTrack && hasData) {
      listingLibPerformanceTracking
        .getPerformanceTracking(MEASURE_MARK_NAMES.CRM_SEARCH)
        .markTimeEnd({ fetch_path: 'pulse' });
    }
  }, [shouldTrack, hasData]);
}

export default function usePulseCrmSearch(
  variables: CrmSearchVariables,
  options: CrmSearchOptions & { enabled: boolean }
) {
  'use memo';
  const { enabled, skip, pollInterval, fetchPolicy, trackPerformance } =
    options;

  const [localPollInterval, setLocalPollInterval] = useState<
    number | undefined
  >(undefined);

  const startPolling = useCallback(
    (pollIntervalMs: number) => setLocalPollInterval(pollIntervalMs),
    []
  );
  const stopPolling = useCallback(() => setLocalPollInterval(undefined), []);

  const isActive = enabled && !skip;

  const {
    data: pulseData,
    loading,
    error,
    stale,
  } = useResource(CrmSearchAllProperties, {
    params: isActive ? getPulseArgs(variables) : skipToken,
    pollInterval: pollInterval ?? localPollInterval,
    passive: fetchPolicy === 'cache-only',
  });

  const objectTypeId = variables.fetchParams.objectTypeId;
  const isTracked = isActive && fetchPolicy !== 'cache-only';

  useEffect(() => {
    if (isTracked && pulseData) {
      Metrics.counter('crm-search-pulse-success', {
        object_type_id: objectTypeId,
      }).increment();
    }
  }, [isTracked, pulseData, objectTypeId]);

  useEffect(() => {
    if (isTracked && error) {
      Metrics.counter('crm-search-pulse-failure', {
        object_type_id: objectTypeId,
        status_code: getStatusCodeDimension(error),
      }).increment();
    }
  }, [isTracked, error, objectTypeId]);

  const data = useMemo(
    () => transformPulseResponseToCrmSearchResponse(pulseData),
    [pulseData]
  );

  useTrackPulsePerf({
    enabled: isActive,
    hasData: pulseData !== undefined,
    trackPerformance,
  });

  return {
    data,
    loading,
    error: error ?? undefined,
    stale,
    startPolling,
    stopPolling,
  };
}
